import { DisplayUnits, UnitSystem } from 'providers/api';
import { i18n } from 'i18n';
import { BasicDisplayUnits, additionalUnitLabels, unitLabels } from 'hooks/useDisplayUnits/useDisplayUnits';
import { frictionLossMetricToImperial, isCustomUnitField, speedMetricToImperial } from 'utils/unitConversions';
import { firstSelectableRheology } from 'utils/rheology';
import type { Model } from '../../hooks/useModelState';
import type { RecipeState } from '../../MineModel/types';
import * as GConstants from '../../MineModel/GConstants';
import * as PCConstants from '../../MineModel/PCConstants';
import { calculateFriction } from '../../canvas/Model/updateFriction';
import { STOPE_SHAPE_COLOURS } from '../../MineModel/Meshes/stopeShapeColours';
import { getStopeLegendItems, getStopeLegendTitle } from '../StopeLegendBuilder/stopeLegend';
import type { StopeLegendConfig } from '../StopeLegendBuilder/stopeLegend';

export type PipeDisplayMode =
  | 'pipeType'
  | 'remainingThickness'
  | 'remainingPercentage'
  | 'wearRate'
  | 'throughputTonnes'
  | null;

interface LegendItem {
  color: string;
  label: string;
  value?: string | number;
}

export const generatePipeTypeLegend = (model: Model, recipeState?: RecipeState): { title: string; items: LegendItem[] } => {
  const unitSystem = model.project?.unitSystemPreference ?? UnitSystem.Metric;
  const customUnitFields = model.project?.customUnitFields ?? [];
  const useImperialFriction = isCustomUnitField(unitSystem, customUnitFields, additionalUnitLabels[DisplayUnits.FrictionLoss].customUnitField);
  const useImperialVelocity = isCustomUnitField(unitSystem, customUnitFields, unitLabels[BasicDisplayUnits.Speed].customUnitField);
  const frictionUnit = useImperialFriction ? 'psi/ft' : 'kPa/m';
  const velocityUnit = useImperialVelocity ? 'ft/s' : 'm/s';

  const items = Object.values(model.pipes).map((pipe) => {
    const parsed = parseInt(pipe.pipeId as unknown as string, 10);
    const idx = Number.isFinite(parsed) ? Math.max(0, parsed - 1) : 0;
    const pipeColour = pipe.colour || GConstants.PIPE_COLOURS[idx] || PCConstants.SPOOL_COLOUR;
    const baseItem = {
      color: pipeColour,
      label: pipe.name || `Pipe ${pipe.pipeId}`,
    };

    if (recipeState && model.rheology && model.rheology.length > 0) {
      const { wetFlowRate, totalDryConcentrationByMass, wetDensity, rheologyDataSetId } = recipeState;

      if (wetFlowRate && totalDryConcentrationByMass && wetDensity) {
        const rheology = model.rheology.find((r) => r.rheologyDataSetId === rheologyDataSetId)
          ?? firstSelectableRheology(model.rheology) ?? model.rheology[0];
        const velocityMetric = pipe.getVelocity(wetFlowRate);
        const frictionMetric = calculateFriction(
          rheology.yieldStressCoefficients.coefficient1,
          rheology.yieldStressCoefficients.coefficient2,
          rheology.viscosityCoefficients.coefficient1,
          rheology.viscosityCoefficients.coefficient2,
          totalDryConcentrationByMass,
          pipe.insideDiameter,
          velocityMetric,
          wetDensity,
          rheology.frictionLossFactorOfSafety,
        );

        const friction = useImperialFriction ? frictionLossMetricToImperial(frictionMetric) : frictionMetric;
        const velocity = useImperialVelocity ? speedMetricToImperial(velocityMetric) : velocityMetric;

        return {
          ...baseItem,
          value: `${friction.toFixed(1)} ${frictionUnit}, ${velocity.toFixed(1)} ${velocityUnit}`,
        };
      }
    }

    return baseItem;
  });

  return {
    title: i18n._('Pipe Types'),
    items,
  };
};

export const generateStopeShapeLegend = (): { title: string; items: LegendItem[] } => ({
  title: i18n._('Stope Shapes'),
  items: [
    { color: STOPE_SHAPE_COLOURS.completed, label: 'Completed / filled' },
    { color: STOPE_SHAPE_COLOURS.active, label: 'Active: green height shows % filled' },
    { color: STOPE_SHAPE_COLOURS.planned, label: 'Planned' },
    { color: STOPE_SHAPE_COLOURS.idle, label: 'No pours' },
  ],
});

export const generateCustomStopeShapeLegend = (config: StopeLegendConfig): { title: string; items: LegendItem[] } => ({
  title: getStopeLegendTitle(config),
  items: getStopeLegendItems(config),
});

const interpolateColor = (color1: string, color2: string, ratio: number): string => {
  const hex1 = color1.replace('#', '');
  const hex2 = color2.replace('#', '');

  const r1 = parseInt(hex1.substr(0, 2), 16);
  const g1 = parseInt(hex1.substr(2, 2), 16);
  const b1 = parseInt(hex1.substr(4, 2), 16);

  const r2 = parseInt(hex2.substr(0, 2), 16);
  const g2 = parseInt(hex2.substr(2, 2), 16);
  const b2 = parseInt(hex2.substr(4, 2), 16);

  const r = Math.round(r1 + (r2 - r1) * ratio);
  const g = Math.round(g1 + (g2 - g1) * ratio);
  const b = Math.round(b1 + (b2 - b1) * ratio);

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

const roundToNiceNumber = (value: number): number => {
  if (value === 0) return 0;

  const magnitude = 10 ** Math.floor(Math.log10(Math.abs(value)));
  const normalized = value / magnitude;

  let niceValue;
  if (normalized <= 1) niceValue = 1;
  else if (normalized <= 2) niceValue = 2;
  else if (normalized <= 5) niceValue = 5;
  else niceValue = 10;

  return niceValue * magnitude;
};

export const generateGradientLegend = (
  title: string,
  minColor: string,
  maxColor: string,
  minValue: number,
  maxValue: number,
  steps: number = 8,
): { title: string; items: LegendItem[] } => {
  const items: LegendItem[] = [];

  const niceMaxValue = roundToNiceNumber(maxValue);
  const niceMinValue = Math.min(minValue, 0);

  items.push({
    color: minColor,
    label: niceMinValue.toString(),
  });

  for (let i = 1; i < steps - 1; i += 1) {
    const ratio = i / (steps - 1);
    const rawValue = niceMinValue + (niceMaxValue - niceMinValue) * ratio;
    const color = interpolateColor(minColor, maxColor, ratio);

    let roundedValue;
    if (niceMaxValue < 1) {
      roundedValue = Number(rawValue.toFixed(3));
    } else if (niceMaxValue < 10) {
      roundedValue = Number(rawValue.toFixed(2));
    } else if (niceMaxValue < 100) {
      roundedValue = Number(rawValue.toFixed(1));
    } else {
      roundedValue = Math.round(rawValue);
    }

    items.push({
      color,
      label: roundedValue.toString(),
    });
  }

  items.push({
    color: maxColor,
    label: niceMaxValue.toString(),
  });

  return { title, items };
};

export const generateWearRateLegend = (
  minValue: number,
  maxValue: number,
): { title: string; items: LegendItem[] } => generateGradientLegend(
  'Wear Rate (mm/Mt)',
  '#00ff00',
  '#ff0000',
  minValue,
  maxValue,
  6,
);

export const generateThroughputLegend = (
  minValue: number,
  maxValue: number,
): { title: string; items: LegendItem[] } => {
  const minValueMt = minValue / 1000000;
  const maxValueMt = maxValue / 1000000;

  const legend = generateGradientLegend(
    'Throughput (Mt)',
    '#00ff00',
    '#ff0000',
    minValueMt,
    maxValueMt,
    6,
  );

  legend.items = legend.items.map((item) => ({
    ...item,
    label: Number(item.label).toFixed(2),
  }));

  return legend;
};

export const generateRemainingThicknessLegend = (
  minValue: number,
  maxValue: number,
): { title: string; items: LegendItem[] } => generateGradientLegend(
  'Remaining Thickness (mm)',
  '#ff0000',
  '#00ff00',
  minValue,
  maxValue,
  6,
);

export const generateRemainingPercentageLegend = (): { title: string; items: LegendItem[] } => ({
  title: i18n._('Remaining Percentage (%)'),
  items: [
    { color: '#ff0000', label: '0%', value: '0%' },
    { color: '#ff8000', label: '25%', value: '25%' },
    { color: '#ffff00', label: '50%', value: '50%' },
    { color: '#80ff00', label: '75%', value: '75%' },
    { color: '#00ff00', label: '100%', value: '100%' },
  ],
});
